/**
 * Dashboard → Solve Sources. Where each student's solved questions came
 * from, and who put them there.
 *
 * Two axes over the same rows, switched by a toggle — see
 * docs/FeatureDesigns/question-source-breakdown.md:
 *   By source — what was solved (practice area, daily plan, exam paper, …)
 *   By who assigned — teacher / self / system / peer / ungranted
 *
 * Not a cross-product grid: six sources × five grant types is thirty columns of
 * mostly zeros. A student's Total is identical under both views, which doubles
 * as a check that the classification isn't dropping work.
 *
 * Columns come from the server so a new session purpose becomes a column here
 * without a code change.
 */

import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataTable, Pagination, SearchFilterBar, type ColumnDef, type SortState, type FilterDef } from '@shared-ui/components';
import { adminApi } from '../../api/adminApi';
import type { QuestionSourceRow } from '../../types';
import './QuestionSourcesTab.css';

const PAGE_SIZE = 25;

const RING_OPTIONS = [
  { value: 'prod', label: 'prod' },
  { value: 'beta', label: 'beta' },
  { value: 'devtesting', label: 'devtesting' },
  { value: 'guest', label: 'guest' },
];
// Synthetic populations are hidden by default (show-set), as on the users list —
// ops numbers that silently include seeded dummies are worse than no numbers.
const SYNTHETIC_OPTIONS = [
  { value: 'dummies', label: 'Dummies' },
  { value: 'agents', label: 'Agents' },
];

type Axis = 'source' | 'grant';
type Metric = 'questions' | 'attempts';

export function QuestionSourcesTab({ days, includeAll }: { days: number; includeAll?: boolean }) {
  const navigate = useNavigate();
  const [axis, setAxis] = useState<Axis>('source');
  const [metric, setMetric] = useState<Metric>('questions');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ sortBy: 'total', sortOrder: 'desc' });
  const [applied, setApplied] = useState<Record<string, string>>({});

  const synthetic = (applied.synthetic || '').split(',').filter(Boolean);

  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: ['questionSources', { days, axis, metric, page, sort, applied }],
    queryFn: () => adminApi.getQuestionSources({
      days,
      axis,
      metric,
      page,
      pageSize: PAGE_SIZE,
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
      search: applied.search || undefined,
      rings: applied.ring || undefined,
      // The page-level audience toggle widens this tab's own synthetic filter;
      // it never narrows it.
      includeAll: includeAll || undefined,
      includeDummies: synthetic.includes('dummies') || undefined,
      includeAgents: synthetic.includes('agents') || undefined,
    }),
    placeholderData: keepPreviousData,
  });

  const columns: ColumnDef<QuestionSourceRow>[] = [
    {
      key: 'student',
      header: 'Student',
      sortable: true,
      mobile: 'title',
      render: (r) => (
        <button className="qs-student-link" onClick={() => navigate(`/users/${r.userId}`)}>
          {r.name}
        </button>
      ),
    },
    ...(data?.columns ?? []).map((c): ColumnDef<QuestionSourceRow> => ({
      key: c.key,
      header: c.label,
      sortable: true,
      className: 'qs-num',
      // A zero is real data here — the point of the table is which buckets are
      // empty — but a page of bold zeros reads as noise, so they're dimmed.
      render: (r) => {
        const v = r.counts[c.key]?.[metric] ?? 0;
        return <span className={v === 0 ? 'qs-zero' : undefined}>{v}</span>;
      },
    })),
    {
      key: 'total',
      header: 'Total',
      sortable: true,
      className: 'qs-num qs-total',
      render: (r) => r.total[metric],
    },
  ];

  const filterDefs: FilterDef[] = [
    { key: 'ring', kind: 'multiselect', placeholder: 'All Rings', options: RING_OPTIONS },
    { key: 'synthetic', kind: 'multiselect', placeholder: 'Real users only', options: SYNTHETIC_OPTIONS },
  ];

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div className="question-sources">
      <div className="qs-controls">
        <div className="qs-toggle-group">
          <span className="qs-toggle-label">View</span>
          <div className="dashboard-time-toggle">
            {([['source', 'By source'], ['grant', 'By who assigned']] as const).map(([v, label]) => (
              <button
                key={v}
                className={`time-toggle-btn ${axis === v ? 'active' : ''}`}
                onClick={() => { setAxis(v); setPage(1); setSort({ sortBy: 'total', sortOrder: 'desc' }); }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="qs-toggle-group">
          <span className="qs-toggle-label">Count</span>
          <div className="dashboard-time-toggle">
            {([['questions', 'Questions'], ['attempts', 'Attempts']] as const).map(([v, label]) => (
              <button
                key={v}
                className={`time-toggle-btn ${metric === v ? 'active' : ''}`}
                onClick={() => setMetric(v)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="qs-hint">
        {axis === 'source'
          ? 'What each student solved. “Practice area” is normal solving outside any test or assignment.'
          : 'Who directed the work. “Ungranted” is everything the student reached on their own.'}
        {' '}Counting {metric === 'questions' ? 'distinct questions' : 'attempts (a retried question counts each time)'}.
      </p>

      <SearchFilterBar
        filters={filterDefs}
        values={applied}
        onChange={(values) => { setApplied(values); setPage(1); }}
        searchPlaceholder="Search name or email..."
        // The pager rides the filter row rather than the table's own toolbar,
        // so filters and paging read as one control strip above one table.
        trailingActions={
          <Pagination page={page} totalPages={totalPages} total={data?.total} onPageChange={setPage} disabled={isLoading} />
        }
      />

      <DataTable
        columns={columns}
        data={data?.rows ?? []}
        rowKey={(r) => r.userId}
        sort={sort}
        onSortChange={(s) => { setSort(s); setPage(1); }}
        loading={isLoading && !isPlaceholderData}
        emptyMessage="No student activity in this range."
      />

      {/* Surfaced rather than swallowed: submissions naming a session we
          couldn't read would otherwise quietly inflate "Practice area". */}
      {!!data?.unresolvedSessions && (
        <p className="qs-warning">
          {data.unresolvedSessions} submission{data.unresolvedSessions === 1 ? '' : 's'} referenced a session
          outside this range and could not be classified.
        </p>
      )}
    </div>
  );
}
